import { LoiForm } from './loi-form';
import {
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS,
    JV_EQUITY_COLUMN_LABELS,
    CORPORATE_EQUITY_COLUMN_LABELS,
    YES_VALUE,
} from './term-form';
import { hasValueForLinkedField } from './has-value-for-linked-field';
import { isLegalIssueVisibleByLogic } from './legal-issue-visibility';

export interface LoiFormErrors {
    termFormFields: Set<string>;
    legalIssueIds: Set<string>;
    additionalCovenantIds: Set<string>;
    loiFileRequired?: boolean;
}

export interface LoiFormValidationOptions {
    documentDetailsId?: string;
    isFsb?: boolean;
    skipLegalTabs?: boolean;
    isAdmin?: boolean;
}

function isNonEmpty(value: string | number | null | undefined): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
}

function isNonNull(value: number | null | undefined): boolean {
    return value !== null && value !== undefined;
}

export function validateLoiForm(formData: LoiForm, options?: LoiFormValidationOptions): LoiFormErrors {
    const termFormFields = new Set<string>();
    const legalIssueIds = new Set<string>();
    const additionalCovenantIds = new Set<string>();
    const loiFileRequired = !options?.documentDetailsId && !options?.isAdmin;

    const termForm = formData.termForm;
    if (termForm) {
        if (!isNonEmpty(termForm.loiExpirationDate)) termFormFields.add('loiExpirationDate');
        if (!isNonNull(termForm.exclusivityPeriodMonth)) termFormFields.add('exclusivityPeriodMonth');

        if (!isNonEmpty(termForm.propertyPurchased?.practicePropertyPurchased)) termFormFields.add('practicePropertyPurchased');
        if (!isNonEmpty(termForm.propertyPurchased?.practiceRealPropertyInclude)) termFormFields.add('practiceRealPropertyInclude');

        const summary = termForm.purchasePriceSummary;
        const cashAtCloser = summary?.cashAtCloser;
        if (!isNonNull(cashAtCloser?.cashAtClose)) termFormFields.add('cashAtClose');
        if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.value)) termFormFields.add('workingCapitalHoldbackValue');
        const wchValue = Number(cashAtCloser?.workingCapitalHoldback?.value) || 0;
        if (wchValue > 0 && !isNonNull(cashAtCloser?.workingCapitalHoldback?.monthsHold)) {
            termFormFields.add('workingCapitalHoldbackMonths');
        }


        const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
        if (!dfpRows.length || !isNonNull(dfpRows[0]?.amount)) termFormFields.add('deferredFixedPayments');

        const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
        if (!dvpRows.length || !isNonNull(dvpRows[0]?.valueAtClosing)) termFormFields.add('deferredVariablePayments');

        if (!isNonNull(summary?.jvEquity?.row?.valueAtClosing)) termFormFields.add('jvEquityValueAtClosing');
        if (!isNonNull(summary?.corporateEquity?.row?.valueAtClosing)) termFormFields.add('corporateEquityValueAtClosing');
        if (!isNonNull(summary?.other)) termFormFields.add('other');

        const dfpTotal = dfpRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        if (dfpTotal > 0) {
            const L = DEFERRED_FIXED_PAYMENT_COLUMN_LABELS;
            for (let i = 0; i < dfpRows.length; i++) {
                const r = dfpRows[i];
                if (!isNonEmpty(r.paymentTiming)) termFormFields.add(`dfp:${i}:${L.paymentTiming}`);
                if (!isNonNull(r.amount)) termFormFields.add(`dfp:${i}:${L.amount}`);
                if (!isNonEmpty(r.annualizedInterestAccrualRate)) termFormFields.add(`dfp:${i}:${L.annualizedInterestAccrualRate}`);
                if (r.contingent === YES_VALUE) {
                    if (!isNonEmpty(r.contingencyDependentVariableValue)) termFormFields.add(`dfp:${i}:${L.contingencyDependentVariable}`);
                    if (!isNonNull(r.contDvMinimumValue)) termFormFields.add(`dfp:${i}:${L.contDvMinimumValue}`);
                }
                if (r.employmentContingency === YES_VALUE) {
                    if (!isNonEmpty(r.tailPeriodForEmployment)) termFormFields.add(`dfp:${i}:${L.tailPeriodForEmployment}`);
                }
            }
        }

        const dvpTotal = dvpRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
        if (dvpTotal > 0) {
            const L = DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS;
            for (let i = 0; i < dvpRows.length; i++) {
                const r = dvpRows[i];
                if (!isNonEmpty(r.paymentTiming)) termFormFields.add(`dvp:${i}:${L.paymentTiming}`);
                if (!isNonEmpty(r.type)) termFormFields.add(`dvp:${i}:${L.type}`);
                if (!isNonNull(r.valueAtClosing)) termFormFields.add(`dvp:${i}:${L.valueAtClosing}`);
                if (!isNonEmpty(r.calculationDependentVariable)) termFormFields.add(`dvp:${i}:${L.calculationDependentVariable}`);
                if (!isNonNull(r.calcDvBaselineValue)) termFormFields.add(`dvp:${i}:${L.calcDvBaselineValue}`);
                if (!isNonEmpty(r.calculationMultiple)) termFormFields.add(`dvp:${i}:${L.calculationMultiple}`);
                if (!isNonEmpty(r.maximumPayment)) termFormFields.add(`dvp:${i}:${L.maximumPayment}`);
                if (r.employmentContingency === YES_VALUE) {
                    if (!isNonEmpty(r.tailPeriodForEmploymentContingency)) termFormFields.add(`dvp:${i}:${L.tailPeriodForEmploymentContingency}`);
                }
                if (!isNonEmpty(r.penaltyMultiple)) termFormFields.add(`dvp:${i}:${L.penaltyMultiple}`);
                if (!isNonEmpty(r.distributionPeriodicity)) termFormFields.add(`dvp:${i}:${L.distributionPeriodicity}`);
            }
        }

        const jvRow = summary?.jvEquity?.row;
        if (jvRow && isNonNull(jvRow.valueAtClosing) && Number(jvRow.valueAtClosing) !== 0) {
            const L = JV_EQUITY_COLUMN_LABELS;
            if (!isNonEmpty(jvRow.paymentTiming)) termFormFields.add(`jv:0:${L.paymentTiming}`);
            if (!isNonEmpty(jvRow.calculationDependentVariable)) termFormFields.add(`jv:0:${L.calculationDependentVariable}`);
            if (!isNonNull(jvRow.calcDvBaselineValue)) termFormFields.add(`jv:0:${L.calcDvBaselineValue}`);
            if (!isNonEmpty(jvRow.calculationMultiple)) termFormFields.add(`jv:0:${L.calculationMultiple}`);
            if (jvRow.employmentContingency === YES_VALUE) {
                if (!isNonEmpty(jvRow.tailPeriodForEmploymentContingency)) termFormFields.add(`jv:0:${L.tailPeriodForEmploymentContingency}`);
            }
            if (!isNonEmpty(jvRow.penaltyMultiple)) termFormFields.add(`jv:0:${L.penaltyMultiple}`);
            if (!isNonEmpty(jvRow.distributionPeriodicity)) termFormFields.add(`jv:0:${L.distributionPeriodicity}`);
        }

        const corpRow = summary?.corporateEquity?.row;
        if (corpRow && isNonNull(corpRow.valueAtClosing) && Number(corpRow.valueAtClosing) !== 0) {
            const L = CORPORATE_EQUITY_COLUMN_LABELS;
            if (!isNonEmpty(corpRow.expectedTimingToRecap)) termFormFields.add(`corp:0:${L.expectedTimingToRecap}`);
            if (!isNonEmpty(corpRow.expectedPayoutAsMultipleOfCurrentValue)) termFormFields.add(`corp:0:${L.expectedPayoutAsMultipleOfCurrentValue}`);
        }

        if (dfpTotal > 0 || dvpTotal > 0) {
            const gdpi = termForm.generalDeferredPaymentsInformation;
            if (!isNonEmpty(gdpi?.paymentObligor)) termFormFields.add('paymentObligor');
            if (!isNonEmpty(gdpi?.assetsHeldByObligor)) termFormFields.add('assetsHeldByObligor');
            if (!isNonEmpty(gdpi?.interestAppliedToDeferredPayments)) termFormFields.add('interestAppliedToDeferredPayments');
        }

        const qualifyingLocations = (termForm.locations ?? []).filter((l) => l.leaseAssumption === 'new');
        for (const loc of qualifyingLocations) {
            const prefix = `lease:${loc.locationId}:`;
            if (!isNonEmpty(loc.termOfLease)) termFormFields.add(`${prefix}termOfLease`);
            if (!isNonEmpty(loc.leaseType)) termFormFields.add(`${prefix}leaseType`);
            if (!isNonEmpty(loc.leaseRate)) termFormFields.add(`${prefix}leaseRate`);
            if (!isNonEmpty(loc.leaseRateEscalation)) termFormFields.add(`${prefix}leaseRateEscalation`);
            if (!isNonEmpty(loc.leaseRateUponExtension)) termFormFields.add(`${prefix}leaseRateUponExtension`);
        }

        if (termForm.isOwnerEmploymentExpected === null || termForm.isOwnerEmploymentExpected === undefined) {
            termFormFields.add('isOwnerEmploymentExpected');
        }
        if (termForm.isEmployeeConsiderationPresent === null || termForm.isEmployeeConsiderationPresent === undefined) {
            termFormFields.add('isEmployeeConsiderationPresent');
        }

        if (termForm.isEmployeeConsiderationPresent === true) {
            if (!isNonNull(cashAtCloser?.considerationForEmployees)) termFormFields.add('cashAtCloserConsiderationForEmployees');
            if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.considerationForEmployees)) termFormFields.add('workingCapitalHoldbackConsiderationForEmployees');

            if (dfpRows.length > 0 && isNonNull(dfpRows[0]?.amount)) {
                if (!isNonNull(dfpRows[0]?.considerationForEmployees)) termFormFields.add('deferredFixedPaymentsConsiderationForEmployees');
            }

            if (dvpRows.length > 0 && isNonNull(dvpRows[0]?.valueAtClosing)) {
                if (!isNonNull(dvpRows[0]?.considerationForEmployees)) termFormFields.add('deferredVariablePaymentsConsiderationForEmployees');
            }

            if (isNonNull(summary?.jvEquity?.row?.valueAtClosing) && Number(summary?.jvEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.jvEquity?.row?.considerationForEmployees)) termFormFields.add('jvEquityConsiderationForEmployees');
            }

            if (isNonNull(summary?.corporateEquity?.row?.valueAtClosing) && Number(summary?.corporateEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.corporateEquity?.row?.considerationForEmployees)) termFormFields.add('corporateEquityConsiderationForEmployees');
            }

            if (isNonNull(summary?.other)) {
                if (!isNonNull(summary?.otherConsiderationForEmployees)) termFormFields.add('otherConsiderationForEmployees');
            }
        }

        if (options?.isFsb) {
            if (!isNonNull(cashAtCloser?.fairValue)) termFormFields.add('cashAtCloserFairValue');
            if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.fairValue)) termFormFields.add('workingCapitalHoldbackFairValue');

            if (dfpRows.length > 0 && isNonNull(dfpRows[0]?.amount)) {
                if (!isNonNull(dfpRows[0]?.fairValue)) termFormFields.add('deferredFixedPaymentsFairValue');
            }

            if (dvpRows.length > 0 && isNonNull(dvpRows[0]?.valueAtClosing)) {
                if (!isNonNull(dvpRows[0]?.fairValue)) termFormFields.add('deferredVariablePaymentsFairValue');
            }

            if (isNonNull(summary?.jvEquity?.row?.valueAtClosing) && Number(summary?.jvEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.jvEquity?.row?.fairValue)) termFormFields.add('jvEquityFairValue');
            }

            if (isNonNull(summary?.corporateEquity?.row?.valueAtClosing) && Number(summary?.corporateEquity?.row?.valueAtClosing) !== 0) {
                if (!isNonNull(summary?.corporateEquity?.row?.fairValue)) termFormFields.add('corporateEquityFairValue');
            }

            if (isNonNull(summary?.other)) {
                if (!isNonNull(summary?.otherFairValue)) termFormFields.add('otherFairValue');
            }
        }
        const amc = termForm.assumptionOfMajorContracts;
        if (amc?.labContract === null || amc?.labContract === undefined) termFormFields.add('labContract');
        if (amc?.operatingLeases === null || amc?.operatingLeases === undefined) termFormFields.add('operatingLeases');
        if (amc?.capitalLeases === null || amc?.capitalLeases === undefined) termFormFields.add('capitalLeases');
        if (amc?.realEstateLease === null || amc?.realEstateLease === undefined) termFormFields.add('realEstateLease');
    } else {
        termFormFields.add('loiExpirationDate');
    }

    if (!options?.skipLegalTabs) {
        const sectionById = new Map(formData.legalIssueSections.map((s) => [s.id, s]));

        for (const item of formData.legalIssues) {
            if (!isLegalIssueVisibleByLogic(formData.legalIssues, item)) continue;
            const section = item.sectionId != null ? sectionById.get(item.sectionId) : undefined;
            if (section?.termsFormLinkedField != null && !hasValueForLinkedField(formData.termForm, section.termsFormLinkedField)) continue;
            if (item.showValueInput !== false && !isNonEmpty(item.value)) {
                legalIssueIds.add(item.id);
            }
        }

        const additionalCovenants = formData.additionalCovenants ?? [];
        for (const item of additionalCovenants) {
            if (!isLegalIssueVisibleByLogic(additionalCovenants, item)) continue;
            const section = item.sectionId != null ? sectionById.get(item.sectionId) : undefined;
            if (section?.termsFormLinkedField != null && !hasValueForLinkedField(formData.termForm, section.termsFormLinkedField)) continue;
            if (item.showValueInput !== false && !isNonEmpty(item.value)) {
                additionalCovenantIds.add(item.id);
            }
        }
    }

    return { termFormFields, legalIssueIds, additionalCovenantIds, loiFileRequired };
}

export function hasLoiFormErrors(errors: LoiFormErrors): boolean {
    return errors.termFormFields.size > 0
        || errors.legalIssueIds.size > 0
        || errors.additionalCovenantIds.size > 0
        || !!errors.loiFileRequired;
}
